import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MaturityBadge, StatusIndicator } from '../../Shared';

/**
 * GovernanceStructure - Full governance hierarchy with drill-down
 * @param {Array} categories - Governance categories
 * @param {object} overallPosture - Overall posture
 * @param {boolean} showSubDomains - Whether to allow drilling into sub-domains
 */
export const GovernanceStructure = ({ categories = [], overallPosture, showSubDomains = true }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (categoryId) => {
    if (showSubDomains) {
      setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    }
  };

  if (!categories || categories.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Estrutura de governança não disponível.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Overall Posture Summary */}
      {overallPosture && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            bgcolor: '#f8fafc',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StatusIndicator status={overallPosture.statusLabel} />
            <MaturityBadge label={overallPosture.maturityLabel} />
            {overallPosture.description && (
              <Typography variant="body2" color="text.secondary">
                {overallPosture.description}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Categories Grid */}
      <Grid container spacing={2}>
        {categories.map((category, index) => (
          <Grid item xs={12} key={category.id || index}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Category Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: showSubDomains && category.subDomains?.length > 0 ? 'pointer' : 'default',
                  '&:hover': showSubDomains && category.subDomains?.length > 0 ? { bgcolor: '#f5f5f5' } : {},
                }}
                onClick={() => toggleCategory(category.id)}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <StatusIndicator status={category.statusLabel} showLabel={false} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {category.name}
                    </Typography>
                    <MaturityBadge label={category.maturityLabel} size="small" />
                  </Box>
                  {category.description && (
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                    </Typography>
                  )}
                </Box>
                {showSubDomains && category.subDomains?.length > 0 && (
                  <IconButton>
                    {expandedCategory === category.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
              </Box>

              {/* Sub-domains */}
              {showSubDomains && category.subDomains?.length > 0 && (
                <Collapse in={expandedCategory === category.id}>
                  <Box sx={{ p: 2, pt: 0, borderTop: '1px solid #e0e0e0' }}>
                    <Grid container spacing={1.5}>
                      {category.subDomains.map((subDomain, subIndex) => (
                        <Grid item xs={12} sm={6} md={4} key={subDomain.id || subIndex}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e8e8e8',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <StatusIndicator status={subDomain.statusLabel} showLabel={false} />
                              <MaturityBadge label={subDomain.maturityLabel} size="small" />
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {subDomain.name}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Collapse>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
